import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import fsaTrialBalanceService from 'api/services/fsa/fsaTrialBalanceService';
import type { NoteClassLive, NoteCrossNoteConfig, NoteLedger } from 'api/types/fsa/noteData.types';
import type { ID } from 'api/types/common.types';

/**
 * FSA-2.2 — Auto cross-note sweep visualisation for schedules whose
 * `cross_note_config.auto_sweep = "sign_anomaly"` (Trade Payables →
 * Advances to Suppliers; Trade Receivables → Advances from Customers).
 *
 * Rendered in the Note Modal's main content area, above the CY Ageing
 * Schedule. Shows the per-period sign-anomalous totals computed live
 * from `liveClasses[*].ledgers`, plus a contributing-ledger expand panel
 * with a per-row "Exclude from sweep" toggle.
 *
 * Live preview vs saved value: this component computes the sweep on
 * the client from `liveClasses` so the auditor sees current numbers
 * BEFORE save (matching what the backend will compute on save). After
 * save, the backend stamps the same numbers onto
 * `class.cross_note_amount` + `prior_cross_note_amount` + the BS
 * picks them up via the existing cross-note pipeline.
 *
 * Sign convention: anomalous = sign opposes schedule's `isCreditBalance`.
 *   - Credit schedules (TP): anomalous = positive (debit) ledger amounts
 *   - Debit schedules (TR):  anomalous = negative (credit) ledger amounts
 *   Display shows |amount| with a sign-flavoured label so the auditor
 *   reads "₹X routed to Advances" without seeing internal sign math.
 */

interface SignSweepSectionProps {
  crossNoteConfig: NoteCrossNoteConfig;
  isCreditBalance: boolean;
  liveClasses: NoteClassLive[];
  consolidatedTbId?: ID | null;
  /** Schedule name (e.g. "Trade Payables") — used in the reconciliation labels. */
  scheduleName?: string;
  /** Called after a successful exclude-toggle so the parent can refetch hydrate. */
  onExcludeChanged?: () => void;
  readOnly?: boolean;
}

interface ContributingLedger {
  classId: string;
  ledger: NoteLedger;
  cyAnomaly: number;
  pyAnomaly: number;
}

const SignSweepSection: React.FC<SignSweepSectionProps> = ({
  crossNoteConfig,
  isCreditBalance,
  liveClasses,
  consolidatedTbId,
  scheduleName,
  onExcludeChanged,
  readOnly = false,
}) => {
  // Reconciliation rows are always visible; the contributing-ledger
  // detail panel still expands on demand.
  const [open, setOpen] = useState(false);
  const [pendingLedgerId, setPendingLedgerId] = useState<string | null>(null);

  // FSA-2.4 — four-figure reconciliation:
  //   • grossCy/Py   = Σ natural-side ledger amounts (displayed positive).
  //                    For TP (credit schedule): sum of credit balances.
  //                    For TR (debit schedule):  sum of debit balances.
  //                    This is what the ageing schedule should sum to.
  //   • keptCy/Py    = Σ anomalous-side amounts on ledgers the auditor
  //                    EXCLUDED from the sweep (displayed positive). They
  //                    stay inside this Note as a contra (e.g. Provision
  //                    for Bad Debts sitting in Trade Receivables).
  //   • sweepCy/Py   = Σ anomalous-side amounts still being swept
  //                    (displayed positive). For TP: debit balances; for
  //                    TR: credit balances → routed to Advances.
  //   • netCy/Py     = Gross − Kept − Sweep. Matches the TB-mapped signed
  //                    total of all ledgers under this note (displayed
  //                    positive in the schedule's natural-sign sense).
  //                    Auditor's reconciliation check against TB.
  //
  // An excluded ledger is a CONTRA, so it REDUCES the Note: the arithmetic
  // is Debit − Credit (natural − anomalous), never Debit + |Credit|. Until
  // 2026-08-21 the excluded amount was added to Gross with its absolute
  // value, which inflated Gross by the contra AND left Net overstated by
  // 2× it — so "Net Balance" no longer matched the TB ledger total or the
  // Effective Ledger the sidebar showed. Effective Ledger / Draft were
  // always right; only this panel double-counted the sign.
  const { grossCy, grossPy, keptCy, keptPy, sweepCy, sweepPy, netCy, netPy, contributors } = useMemo(() => {
    const list: ContributingLedger[] = [];
    let gCy = 0, gPy = 0;
    let kCy = 0, kPy = 0;
    let sCy = 0, sPy = 0;

    for (const cls of liveClasses ?? []) {
      for (const ledger of cls.ledgers ?? []) {
        if (ledger.isControlAccount) continue;

        const closing = Number(ledger.closingActual ?? 0);
        const opening = Number(ledger.openingActual ?? 0);

        // Natural-side contribution (always positive for display).
        // TP (credit): negative closing → |closing| adds to Gross.
        // TR (debit):  positive closing → closing adds to Gross.
        const cyNatural = isCreditBalance
          ? (closing < 0 ? -closing : 0)
          : (closing > 0 ? closing : 0);
        const pyNatural = isCreditBalance
          ? (opening < 0 ? -opening : 0)
          : (opening > 0 ? opening : 0);
        gCy += cyNatural;
        gPy += pyNatural;

        // Anomalous-side contribution (always positive for display).
        const cyAnomaly = isCreditBalance
          ? (closing > 0 ? closing : 0)
          : (closing < 0 ? -closing : 0);
        const pyAnomaly = isCreditBalance
          ? (opening > 0 ? opening : 0)
          : (opening < 0 ? -opening : 0);

        // Excluded ledgers stay inside this Note as a contra — netted off
        // on their own line — instead of being routed to the counterpart.
        if (ledger.excludeFromSignSweep) {
          kCy += cyAnomaly;
          kPy += pyAnomaly;
        } else {
          sCy += cyAnomaly;
          sPy += pyAnomaly;
        }

        if (cyAnomaly !== 0 || pyAnomaly !== 0) {
          list.push({
            classId: String(cls.classId),
            ledger,
            cyAnomaly,
            pyAnomaly,
          });
        }
      }
    }
    return {
      grossCy: gCy, grossPy: gPy,
      keptCy:  kCy, keptPy:  kPy,
      sweepCy: sCy, sweepPy: sPy,
      netCy:   gCy - kCy - sCy,
      netPy:   gPy - kPy - sPy,
      contributors: list,
    };
  }, [liveClasses, isCreditBalance]);

  const hasAnomaly = contributors.length > 0;
  // Cy/Py totals retained for downstream callers / API compatibility.
  const cyTotal = sweepCy;
  const pyTotal = sweepPy;

  const handleToggleExclude = async (ledgerId: string, currentlyExcluded: boolean) => {
    if (!consolidatedTbId) {
      notificationService.toast.warning('Upload a Trial Balance first.');
      return;
    }
    setPendingLedgerId(ledgerId);
    try {
      await fsaTrialBalanceService.bulkExcludeFromSignSweep(consolidatedTbId, {
        entryIds: [ledgerId],
        excludeFromSignSweep: !currentlyExcluded,
      });
      notificationService.toast.success(
        currentlyExcluded ? 'Ledger re-included in sign sweep.' : 'Ledger excluded from sign sweep.'
      );
      onExcludeChanged?.();
    } catch {
      notificationService.toast.error('Could not toggle sign-sweep flag.');
    } finally {
      setPendingLedgerId(null);
    }
  };

  const fmt = (n: number) =>
    Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Auditor-facing reconciliation labels. Phrasing matches Schedule III
  // disclosure language; the natural-side qualifier (credit / debit) is
  // derived from the schedule's sign convention.
  const noteName = scheduleName ?? (isCreditBalance ? 'Trade Payables' : 'Trade Receivables');
  const naturalSideLabel = isCreditBalance ? 'credit balances only' : 'debit balances only';
  const sweepTargetLabel = crossNoteConfig.label
    ?? (isCreditBalance ? 'Advances (debit balances)' : 'Advances (credit balances)');
  // Contra row wording mirrors the sweep row: it names the OPPOSITE side to
  // the schedule's natural one, because that's what an excluded ledger holds.
  const keptSideLabel = isCreditBalance ? 'Debit balances' : 'Credit balances';
  const hasKept = keptCy !== 0 || keptPy !== 0;
  const keptCount = contributors.filter(c => c.ledger.excludeFromSignSweep).length;

  return (
    <div style={containerStyle}>
      {/* Header strip — always visible. Click toggles the contributing-
          ledger detail panel below. */}
      <div style={reconHeaderStyle}>
        <span style={titleStyle}>↪ <strong>Reconciliation</strong></span>
      </div>

      {/* Reconciliation — always visible. Gives the auditor:
          (1) the Gross amount that should match the ageing schedule total;
          (2) any opposite-side balances the auditor kept in this Note,
              netted off here (row hidden when there are none);
          (3) the Advance amount routed out to the counterpart Note;
          (4) the Net amount that should match the TB ledger total for
          all ledgers classified to this Note. */}
      <div style={reconBodyStyle}>
        <table style={reconTableStyle}>
          <thead>
            <tr>
              <th style={reconThLeft}></th>
              <th style={reconThRight}>CY</th>
              <th style={reconThRight}>PY</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={reconTdLabel}>
                Gross {noteName} <span style={reconHintStyle}>({naturalSideLabel})</span>
                <div style={reconSubHintStyle}>matches the ageing schedule total</div>
              </td>
              <td style={reconTdAmount}>{fmt(grossCy)}</td>
              <td style={reconTdAmount}>{fmt(grossPy)}</td>
            </tr>
            {/* Contra row — only when the auditor has kept anomalous
                balances inside this Note. Netted off here rather than
                routed out, so Net still reconciles to the TB total. */}
            {hasKept && (
              <tr>
                <td style={reconTdLabel}>
                  Less: {keptSideLabel} retained in this Note
                  <div style={reconSubHintStyle}>excluded from the sweep — netted off within this Note</div>
                </td>
                <td style={reconTdAmount}>{fmt(keptCy)}</td>
                <td style={reconTdAmount}>{fmt(keptPy)}</td>
              </tr>
            )}
            <tr>
              <td style={reconTdLabel}>
                Less: {sweepTargetLabel}
                <div style={reconSubHintStyle}>routed to the counterpart Note</div>
              </td>
              <td style={reconTdAmount}>{fmt(sweepCy)}</td>
              <td style={reconTdAmount}>{fmt(sweepPy)}</td>
            </tr>
            <tr style={reconNetRowStyle}>
              <td style={{ ...reconTdLabel, fontWeight: 700 }}>
                Net Balance
                <div style={reconSubHintStyle}>matches the TB ledger total for this Note</div>
              </td>
              <td style={{ ...reconTdAmount, fontWeight: 700 }}>{fmt(netCy)}</td>
              <td style={{ ...reconTdAmount, fontWeight: 700 }}>{fmt(netPy)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Contributing-ledger detail — toggleable expand. Shows the ledgers
          that contribute to the Advance amount with per-row exclude
          action. Auto-hidden when no sign-anomalous balances exist. */}
      {hasAnomaly && (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={contribToggleStyle}
          title={open ? 'Hide contributing ledgers' : 'View contributing ledgers'}
        >
          <IconSystem.chevronRight
            size={12}
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
          />
          {contributors.length} ledger{contributors.length === 1 ? '' : 's'} with {keptSideLabel.toLowerCase()}
          {keptCount > 0
            ? ` — ${contributors.length - keptCount} routed to the Advance, ${keptCount} kept in this Note`
            : ' contributing to the Advance'}
        </button>
      )}

      {open && hasAnomaly && (
        <div style={bodyStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thLeft}>Ledger</th>
                  <th style={thRight}>CY</th>
                  <th style={thRight}>PY</th>
                  <th style={thCenter}>Action</th>
                </tr>
              </thead>
              <tbody>
                {contributors.map(({ ledger, cyAnomaly, pyAnomaly }) => {
                  const excluded = ledger.excludeFromSignSweep;
                  const isPending = pendingLedgerId === String(ledger.ledgerId);
                  return (
                    <tr key={String(ledger.ledgerId)} style={excluded ? excludedRowStyle : undefined}>
                      <td style={tdLeft}>
                        <span>{ledger.ledgerName}</span>
                        {excluded && (
                          <span style={excludedBadgeStyle} title="Kept inside this Note as a contra">
                            kept in note
                          </span>
                        )}
                      </td>
                      <td style={tdRight}>{cyAnomaly !== 0 ? fmt(cyAnomaly) : '—'}</td>
                      <td style={tdRight}>{pyAnomaly !== 0 ? fmt(pyAnomaly) : '—'}</td>
                      <td style={tdCenter}>
                        <button
                          type="button"
                          disabled={readOnly || isPending || !consolidatedTbId}
                          onClick={() => handleToggleExclude(String(ledger.ledgerId), excluded)}
                          style={actionButtonStyle(excluded)}
                          title={excluded ? 'Re-include in auto-sweep' : 'Exclude from auto-sweep'}
                        >
                          {isPending ? '…' : excluded ? 'Re-include' : 'Exclude'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </div>
      )}
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  marginBottom: 12,
  border: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: 6,
  background: 'var(--bg-primary, #fff)',
};

const reconHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  background: 'var(--bg-secondary, #f8fafc)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: '6px 6px 0 0',
  fontSize: '0.875rem',
  fontWeight: 500,
};

const reconBodyStyle: React.CSSProperties = {
  padding: '6px 12px 8px',
};

const reconTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8rem',
};

const reconThLeft: React.CSSProperties = {
  textAlign: 'left',
  padding: '2px 8px 4px',
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-secondary, #64748b)',
};
const reconThRight: React.CSSProperties = { ...reconThLeft, textAlign: 'right', width: 130 };

const reconTdLabel: React.CSSProperties = {
  padding: '4px 8px',
  color: 'var(--text-primary, #1e293b)',
};
const reconTdAmount: React.CSSProperties = {
  padding: '4px 8px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
};
const reconNetRowStyle: React.CSSProperties = {
  borderTop: '1px solid var(--border-color, #e2e8f0)',
  background: 'var(--bg-secondary, #f8fafc)',
};
const reconHintStyle: React.CSSProperties = {
  fontWeight: 400,
  fontStyle: 'italic',
  color: 'var(--text-secondary, #64748b)',
  fontSize: '0.72rem',
};
const reconSubHintStyle: React.CSSProperties = {
  fontSize: '0.68rem',
  color: 'var(--text-secondary, #64748b)',
  fontWeight: 400,
  marginTop: 1,
};

const contribToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  padding: '4px 12px',
  background: 'transparent',
  border: 'none',
  borderTop: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: 0,
  cursor: 'pointer',
  textAlign: 'left',
  color: 'var(--text-secondary, #64748b)',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 500,
};

const titleStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  flexShrink: 0,
};

const bodyStyle: React.CSSProperties = {
  padding: 12,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
};

const thLeft: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  fontWeight: 500,
  color: 'var(--text-secondary, #64748b)',
};
const thRight: React.CSSProperties = { ...thLeft, textAlign: 'right' };
const thCenter: React.CSSProperties = { ...thLeft, textAlign: 'center', width: 110 };

const tdLeft: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-color, #f1f5f9)',
};
const tdRight: React.CSSProperties = {
  ...tdLeft,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
const tdCenter: React.CSSProperties = { ...tdLeft, textAlign: 'center' };

const excludedRowStyle: React.CSSProperties = {
  background: 'var(--bg-tertiary, #fef3c7)',
  opacity: 0.75,
};

const excludedBadgeStyle: React.CSSProperties = {
  marginLeft: 8,
  padding: '1px 6px',
  borderRadius: 3,
  background: 'var(--warning-bg, #fde68a)',
  color: 'var(--warning-text, #92400e)',
  fontSize: '0.6875rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
};

const actionButtonStyle = (excluded: boolean): React.CSSProperties => ({
  padding: '3px 10px',
  border: `1px solid ${excluded ? 'var(--success-text, #15803d)' : 'var(--text-secondary, #64748b)'}`,
  borderRadius: 4,
  background: 'var(--bg-primary, #fff)',
  color: excluded ? 'var(--success-text, #15803d)' : 'var(--text-primary, #1e293b)',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 500,
});

export default SignSweepSection;
